import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useApiError } from "@/lib/useApiError";

const NIET_GEBOUWD = [404, 405, 501];

/** Wachtwoord wijzigen in Mijn account. Het CRM bewaart en controleert het wachtwoord. */
export const PasswordSection = () => {
  const { t } = useTranslation();
  const { wachtwoordWijzigen } = useAuth();
  const toMessage = useApiError();

  const [open, setOpen] = useState(false);
  const [huidig, setHuidig] = useState("");
  const [nieuw, setNieuw] = useState("");
  const [herhaling, setHerhaling] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setHuidig("");
    setNieuw("");
    setHerhaling("");
    setError("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (nieuw.length < 8) {
      setError(t("auth.reset.tooShort"));
      return;
    }
    if (nieuw !== herhaling) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await wachtwoordWijzigen(huidig, nieuw);
      toast.success(t("account.password.success"));
      reset();
      setOpen(false);
    } catch (err) {
      if (NIET_GEBOUWD.includes(err?.status)) setError(t("account.password.unavailable"));
      else if (err?.status === 400 || err?.status === 403) setError(t("account.password.wrongCurrent"));
      else setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="account-password-section">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">
        {t("account.password.title")}
      </p>

      {!open ? (
        <div className="mt-3">
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="account-password-open-button">
            <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("account.password.change")}
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 max-w-sm space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="account-password-error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="account-password-current">{t("account.password.current")}</Label>
            <Input
              id="account-password-current"
              type="password"
              autoComplete="current-password"
              required
              value={huidig}
              onChange={(e) => setHuidig(e.target.value)}
              data-testid="account-password-current-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-password-new">{t("auth.reset.password")}</Label>
            <Input
              id="account-password-new"
              type="password"
              autoComplete="new-password"
              required
              value={nieuw}
              onChange={(e) => setNieuw(e.target.value)}
              data-testid="account-password-new-input"
            />
            <p className="text-xs text-muted-foreground">{t("auth.reset.hint")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-password-confirm">{t("auth.reset.confirm")}</Label>
            <Input
              id="account-password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={herhaling}
              onChange={(e) => setHerhaling(e.target.value)}
              data-testid="account-password-confirm-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={submitting} data-testid="account-password-submit-button">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? t("auth.reset.submitting") : t("auth.reset.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              data-testid="account-password-cancel-button"
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
